import {
    PrequalApplication,
    LoanApplicant,
    Address,
    ResidenceOwnership,
    PropertyType,
    ResidenceType,
    EmploymentStatus,
    Citizenship,
    SpokenLanguage,
    LoanApplicationSubmitSource,
    LoanApplicantIdDocument,
    ApplicantEmployment,
    Spouse,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    ApplicationData,
    calculateIncomes,
    sanitizeFieldValue,
} from '@servicetitan/financing-application-shared';
import { FIRST_LOOK_RESIDENCE_OWNERSHIP } from '@servicetitan/financing-workflow-shared';
import { formatAmount } from '@servicetitan/financing-utils-shared';

export interface PreparePrequalificationDataParams {
    applicationData: ApplicationData;
    prequalId?: string;
    tenantId: string;
    buId: string;
    merchantId?: string;
    planNumbers?: string[];
    requestedAmount?: number;
    technicianLastName?: string;
    technicianPhone?: string;
    technicianEmail?: string;
    fingerPrint?: string;
    source: LoanApplicationSubmitSource;
    hasMailingAddressStep?: boolean;
}

// Format phone number to remove special characters
const formatPhoneNumber = (phone: string | undefined): string | undefined => {
    if (!phone) {
        return undefined;
    }
    return phone.replace(/[\(\)\s-]/g, '');
};

// Format SSN to remove dashes
const sanitizeSSN = (ssn: string | undefined): string | undefined => {
    if (!ssn) {
        return undefined;
    }
    return ssn.replace(/-/g, '');
};

export const preparePrequalificationData = ({
    applicationData,
    prequalId,
    tenantId,
    buId,
    merchantId,
    planNumbers,
    requestedAmount,
    technicianLastName,
    technicianPhone,
    technicianEmail,
    fingerPrint,
    source,
    hasMailingAddressStep,
}: PreparePrequalificationDataParams): PrequalApplication => {
    const primaryJobDetails = applicationData.applicant.primaryApplicant?.jobDetails;
    const jobAddress = primaryJobDetails?.address;

    // Prefer a real mailing/home address over the job address; gated on the boolean so a
    // lingering override address left in the store after unchecking is ignored.
    const homeAddress = hasMailingAddressStep
        ? applicationData.mailingAddress.primaryApplicant?.address
        : primaryJobDetails?.mailingAddressDiffersFromJobAddress
          ? primaryJobDetails?.mailingAddress?.address
          : jobAddress;

    // Property address never follows the override; it stays the job/service address.
    const propertyAddress = hasMailingAddressStep
        ? applicationData.mailingAddress.workPerformedAtAddress
            ? applicationData.mailingAddress.primaryApplicant?.address
            : applicationData.serviceProperty.address
        : jobAddress;

    const hasCoApplicant = applicationData.coApplicant.hasCoApplicant;

    const primaryApplicantData = applicationData.applicant.primaryApplicant;
    const coApplicantData = hasCoApplicant ? applicationData.coApplicant.coApplicant : undefined;

    // FIN-15610 - sometimes number fields are formatted as strings, seems limited to Safari and Mobile Safari
    let monthlyHousingPayment = 0;
    if (
        applicationData.serviceProperty.primaryApplicant &&
        'monthlyHousingPayment' in applicationData.serviceProperty.primaryApplicant
    ) {
        monthlyHousingPayment =
            applicationData.serviceProperty.primaryApplicant.monthlyHousingPayment ?? 0;
        monthlyHousingPayment =
            typeof monthlyHousingPayment === 'string'
                ? Number(formatAmount(monthlyHousingPayment))
                : monthlyHousingPayment;
    }

    const { grossMonthlyIncome, annualIncome } = calculateIncomes({
        grossMonthlyIncome: primaryApplicantData?.employment?.grossMonthlyIncome,
        annualIncome: primaryApplicantData?.annualIncome,
    });

    // Handle spouse info
    let spouseInfo: Spouse | undefined;
    if (primaryApplicantData?.jobDetails?.spouseInfo?.isAddressSameAsApplicant) {
        spouseInfo = {
            ...primaryApplicantData.jobDetails.spouseInfo,
            address: homeAddress,
        };
    } else if (primaryApplicantData?.jobDetails?.spouseInfo) {
        spouseInfo = primaryApplicantData.jobDetails.spouseInfo;
    }

    // Build primary applicant
    const primaryApplicant: LoanApplicant = {
        firstName: primaryApplicantData?.firstName,
        middleInitial: primaryApplicantData?.middleInitial,
        lastName: primaryApplicantData?.lastName,
        dateOfBirth: primaryApplicantData?.dateOfBirth ?? '',
        ssn: sanitizeSSN(primaryApplicantData?.ssn),
        emailAddress: primaryApplicantData?.email,
        primaryPhone: formatPhoneNumber(primaryApplicantData?.primaryPhone),
        alternatePhone: formatPhoneNumber(primaryApplicantData?.alternatePhone),
        workPhone: formatPhoneNumber(primaryApplicantData?.workPhone),
        isMarried:
            applicationData.mailingAddress.primaryApplicant?.isMarried ??
            primaryApplicantData?.jobDetails?.spouseInfo?.isMarried,
        housingType: (sanitizeFieldValue(
            primaryApplicantData?.jobDetails?.residenceType ??
                applicationData.serviceProperty.primaryApplicant?.housingType,
            FIRST_LOOK_RESIDENCE_OWNERSHIP,
        ) ?? ResidenceOwnership.NotSet) as ResidenceOwnership,
        monthlyHousingPayment,
        propertyType:
            (applicationData.serviceProperty.primaryApplicant?.propertyType as PropertyType) ??
            PropertyType.NotSet,
        address: homeAddress as Address,
        applicantId: primaryApplicantData?.applicantId as LoanApplicantIdDocument | undefined,
        employment: {
            grossMonthlyIncome,
            employerName: primaryApplicantData?.employment?.employerName,
            employerPhone: formatPhoneNumber(primaryApplicantData?.employment?.employerPhone),
        } as ApplicantEmployment,
        spouseContact: applicationData.mailingAddress.primaryApplicant?.spouseContact,
        annualIncome,
        residenceType: (primaryApplicantData?.typeOfHome as ResidenceType) ?? ResidenceType.NotSet,
        employmentStatus:
            (primaryApplicantData?.employment?.status as EmploymentStatus) ??
            EmploymentStatus.NotSet,
        citizenship: (primaryApplicantData?.citizenship as Citizenship) ?? Citizenship.NotSet,
        spokenLanguage:
            (primaryApplicantData?.spokenLanguage as SpokenLanguage) ?? SpokenLanguage.NotSet,
        signature: primaryApplicantData?.signature,
        spouse: spouseInfo,
    };

    // Build co-applicant if present
    let coApplicant: LoanApplicant | undefined;
    if (hasCoApplicant && coApplicantData) {
        const {
            grossMonthlyIncome: coApplicantGrossMonthlyIncome,
            annualIncome: coApplicantAnnualIncome,
        } = calculateIncomes({
            grossMonthlyIncome: coApplicantData.employment?.grossMonthlyIncome,
            annualIncome: coApplicantData.annualIncome,
        });

        const coApplicantAddress =
            coApplicantData.isAddressSameAsPrimary === true ? homeAddress : coApplicantData.address;

        let coApplicantSpouseInfo: Spouse | undefined;
        if (coApplicantData?.spouseInfo?.isMarried) {
            if (coApplicantData.spouseInfo.isAddressSameAsApplicant) {
                coApplicantSpouseInfo = {
                    firstName: coApplicantData.spouseInfo.firstName,
                    lastName: coApplicantData.spouseInfo.lastName,
                    address: coApplicantAddress,
                };
            } else if (coApplicantData.spouseInfo) {
                coApplicantSpouseInfo = {
                    firstName: coApplicantData.spouseInfo.firstName,
                    lastName: coApplicantData.spouseInfo.lastName,
                    address: coApplicantData.spouseInfo.address,
                };
            }
        }

        coApplicant = {
            firstName: coApplicantData.firstName,
            middleInitial: coApplicantData.middleInitial,
            lastName: coApplicantData.lastName,
            dateOfBirth: coApplicantData.dateOfBirth!,
            ssn: sanitizeSSN(coApplicantData.ssn),
            emailAddress: coApplicantData.email,
            primaryPhone: formatPhoneNumber(coApplicantData.primaryPhone),
            alternatePhone: formatPhoneNumber(coApplicantData.alternatePhone),
            workPhone: formatPhoneNumber(coApplicantData.workPhone),
            isMarried: coApplicantData.isMarried ?? coApplicantData.spouseInfo?.isMarried,
            housingType: (sanitizeFieldValue(
                coApplicantData.jobDetails?.residenceType,
                FIRST_LOOK_RESIDENCE_OWNERSHIP,
            ) ?? ResidenceOwnership.NotSet) as ResidenceOwnership,
            monthlyHousingPayment: 0,
            propertyType: PropertyType.NotSet,
            address: coApplicantAddress as Address,
            applicantId: coApplicantData.applicantId as LoanApplicantIdDocument | undefined,
            employment: {
                grossMonthlyIncome: coApplicantGrossMonthlyIncome,
                employerName: coApplicantData.employment?.employerName,
                employerPhone: formatPhoneNumber(coApplicantData.employment?.employerPhone),
            } as ApplicantEmployment,
            annualIncome: coApplicantAnnualIncome,
            residenceType: (coApplicantData.typeOfHome as ResidenceType) ?? ResidenceType.NotSet,
            employmentStatus:
                (coApplicantData.employment?.status as EmploymentStatus) ?? EmploymentStatus.NotSet,
            citizenship: (coApplicantData.citizenship as Citizenship) ?? Citizenship.NotSet,
            spokenLanguage:
                (coApplicantData.spokenLanguage as SpokenLanguage) ?? SpokenLanguage.NotSet,
            signature: coApplicantData.signature,
            spouseContact: coApplicantData.spouseContact,
            spouse: coApplicantSpouseInfo,
        };
    }

    return {
        prequalId,
        tenantId,
        buId,
        merchantId,
        planNumbers,
        requestedAmount,
        technicianLastName,
        technicianPhone: formatPhoneNumber(technicianPhone),
        technicianEmail,
        fingerPrint,
        primaryApplicant,
        coApplicant,
        address: propertyAddress as Address,
        source,
    };
};
